import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import PayrollAdd from "./AdvanceAdd";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import AdvanceEdit from "./AdvanceEdit";

const AdvanceIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination and filtering states
  const [searchTerm, setSearchTerm] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // Fetch advance records with search and pagination.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["advance", searchTerm, selectedEmployee, selectedYear, selectedMonth, page, rowsPerPage],
    `shop/advances/?search=${searchTerm}&employee=${selectedEmployee}&year=${selectedYear}&month=${selectedMonth}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Fetch employees data
  const { data: employeeData } = useFetchObjects(
    ["employee-data"],
    `admin/users/?search=${searchUser}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/advances", token);

  useEffect(() => {
    refetch();
  }, [
    searchTerm,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    page,
    rowsPerPage,
    refetch,
  ]);

  // Determine payroll list and total count (using DRF paginated response)
  const payrolls = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : payrolls.length;

  if (isError) {
    return <ErrorPage message={t("AdvanceIndex.failedToLoad")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">
          {t("AdvanceIndex.advance_list_title")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("AdvanceIndex.advance_add_new")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("AdvanceIndex.advance_data_loaded_on")} {new Date().toLocaleString()}
      </Typography>

      {/* Search and Filter Fields */}
      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("AdvanceIndex.advance_search_placeholder")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <Select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">All Employees</MenuItem>
            {(employeeData?.results || employeeData || []).map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee?.first_name && employee?.last_name
                  ? `${employee?.first_name} ${employee?.last_name}`
                  : employee.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" sx={{ minWidth: 100 }}>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Year *</MenuItem>
            {Array.from({length: 31}, (_, i) => 2020 + i).map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" sx={{ minWidth: 100 }}>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Month *</MenuItem>
            {Array.from({length: 12}, (_, i) => i + 1).map(month => (
              <MenuItem key={month} value={month}>
                {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Advance Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="advance table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_id")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_employee")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_phone")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_advance")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_year")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_month")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_date")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("AdvanceIndex.advance_table_action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {Array.from(new Array(9)).map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payrolls.map((payroll, index) => (
                  <TableRow key={payroll.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{payroll?.employee_details?.name}</TableCell>
                    <TableCell>{payroll?.employee_details?.contact}</TableCell>
                    <TableCell>
                      {payroll?.amount || 0}{" "}
                      {payroll?.currency_details?.title}
                    </TableCell>
                    <TableCell>{payroll?.year}</TableCell>
                    <TableCell>{payroll?.month}</TableCell>
                    <TableCell>
                      {new Date(payroll?.payment_date).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(payroll);
                          setOpenEdit(true);
                        }}
                      >
                        {t("AdvanceIndex.advance_edit_button")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(payroll.id)}
                      >
                        {t("AdvanceIndex.advance_delete_button")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("AdvanceIndex.advance_rows_per_page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      {openAdd && (
        <PayrollAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openEdit && (
        <AdvanceEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
};

export default AdvanceIndex;
